import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Send, CheckCircle, AlertCircle, PlayCircle, Loader2 } from 'lucide-react';
import api from '../api/api'; // Centralized API import

export default function BatchSend() {
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [batchStatus, setBatchStatus] = useState('');

    const handleSend = async () => {
        setSuccess('');
        setError('');
        setLoading(true);
        setBatchStatus('');

        try {
            // Use the centralized api instance for the POST request
            const res = await api.post('/api/batch-send');
            setSuccess(res.data.message);
            setBatchStatus(res.data.status);

            // clear success message after 5 seconds
            setTimeout(() => {
                setSuccess('');
                setBatchStatus('');
            }, 5000);

        } catch (err) {
            console.error('Batch send error:', err);
            setError(err.response?.data?.error || 'Batch could not be started! Please try again.');
        }

        setLoading(false);
    };

    return (
        <div className="flex justify-center items-center min-h-[calc(100vh-100px)] p-4 bg-muted/10">
            <Card className="w-full max-w-lg shadow-xl border-t-4 border-t-primary">
                <CardContent className="pt-8 flex flex-col items-center text-center">
                    <div className="mb-6 rounded-full bg-primary/10 p-4">
                        <Send className="h-10 w-10 text-primary" />
                    </div>

                    <CardTitle className="text-2xl font-bold mb-2">
                        Batch Email Sending
                    </CardTitle>

                    <CardDescription className="mb-6 max-w-sm">
                        Start sending 80 emails with one click. This process runs in the background and its progress is updated on the dashboard.
                    </CardDescription>

                    <div className="flex gap-2 mb-8">
                        <Badge variant="secondary" className="px-3 py-1 gap-1 text-sm">
                            <Send className="h-3 w-3" /> 80 Email
                        </Badge>
                        <Badge variant="outline" className="px-3 py-1 gap-1 text-sm border-green-500 text-green-600 bg-green-50 dark:bg-green-900/20">
                            <CheckCircle className="h-3 w-3" /> Worker Thread
                        </Badge>
                    </div>

                    <Button
                        size="lg"
                        className="w-full max-w-xs text-lg font-semibold h-12 shadow-md hover:shadow-lg transition-all"
                        disabled={loading}
                        onClick={handleSend}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                Starting...
                            </>
                        ) : (
                            <>
                                <PlayCircle className="mr-2 h-5 w-5" />
                                Start Sending 80 Emails
                            </>
                        )}
                    </Button>

                    {loading && (
                        <div className="w-full mt-6 space-y-2">
                            <Progress value={undefined} className="h-2 w-full animate-pulse" />
                            <p className="text-sm text-muted-foreground animate-pulse">Worker thread is starting...</p>
                        </div>
                    )}

                    {success && (
                        <Alert className="mt-6 bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-900/50 text-left">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <AlertTitle className="text-green-700 dark:text-green-500">Success</AlertTitle>
                            <AlertDescription className="text-green-700 dark:text-green-500">
                                {success}
                                {batchStatus && <div className="mt-1 text-xs opacity-80">Status: {batchStatus}</div>}
                            </AlertDescription>
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="destructive" className="mt-6 text-left">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>
                                {error}
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="mt-8 bg-muted/50 p-4 rounded-lg w-full">
                        <p className="text-sm text-muted-foreground">
                            💡 Tip: Email sending continues in the background.
                            You can track the progress on the <b>Dashboard</b> page.
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
} 